import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  activateDeactivateUser,
  createUser,
  deleteUser,
  fetchAllRoles,
  fetchBranches,
  fetchCountry,
  fetchGenders,
  fetchRoles,
  fetchUsers,
  fetchUsersTableColumnNames,
  updateUser,
  updateUserPreference,
} from "../../../slices/thunks";
import { getAuthUser } from "../../../helpers/utils";
import CommonTable from "../../../Components/Common/Table/CommonTable";
import AddModal from "../../../Components/Common/Modal/AddModal";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import DeleteModal from "../../../Components/Common/DeleteModal";
import AddSystemUserForm from "../../../Components/Common/UserForm/AddSystemUserForm";
import { getRoles } from "@testing-library/react";
import CommonAddModal from "../../../Components/Common/Modal/CommonAddModal";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";
import ConfirmationModal from "../../../Components/Common/ConfirmationModal";

const labels = [
  {
    id: 1,
    labelName: "FirstName",
    fieldName: "text-box",
    name: "FirstName",
    value: "FirstName",
    required: true,
    minLength:3,
    maxLength: 50,
    errorMessage: "First Name is required",
  },
  {
    id: 2,
    labelName: "LastName",
    fieldName: "text-box",
    name: "LastName",
    value: "LastName",
    required: true,
    minLength:3,
    maxLength: 50,
    errorMessage: "Last Name is required",
  },
  {
    id: 3,
    labelName: "Email",
    fieldName: "email-box",
    type: "email",
    name: "Email",
    value: "Email",
    required: true,
    errorMessage: "Email is required",
  },
  {
    id: 5,
    labelName: "Mobile",
    fieldName: "mobile-box2",
    type: "tel",
    name: "Phone",
    value: "Phone",
    required: false,
    errorMessage: "Mobile no. is required",
  },
  {
    id: 6,
    labelName: "Gender",
    fieldName: "genders",
    type: "select",
    name: "Gender",
    value: "GenderID",
    required: true,
    errorMessage: "Gender is required",
  },
  {
    id: 7,
    labelName: "Roles",
    fieldName: "Roles",
    type: "select",
    name: "RoleID",
    value: "RoleID",
    required: true,
    errorMessage: "Roles is required",
  }
];

const actionConfig = {
  edit: {
    icon: "ri-edit-2-line",
    tooltip: "Edit",
  },
  delete: {
    icon: "ri-delete-bin-2-line",
    tooltip: "Delete",
  },
  // permission: {
  //   icon: "ri-lock-unlock-line",
  //   tooltip: "Permission",
  //   // isVisible: (item) => item.Id != 2,
  // },
};

function SystemUser() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const {user} = getAuthUser();
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddOrEdit, setIsAddOrEdit] = useState("isAdd");
  const [selectedRow, setSelectedRow] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [User, setUser] = useState();
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [userToToggle, setUserToToggle] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);


  const { users, loading, error, tableColumnNames, module } = useSelector(
    (state) => ({
      users: state.Users.users,
      tableColumnNames: state.Users.tableColumnNames,
      loading: state.Users.loading,
      error: state.Users.error,
      module: state.Module.module,
    })
  );


  const handleClassesClicks = (value) => {
    setShow(true);
    setIsAddOrEdit(value);
    setSelectedRow({});
  };

  const onEditClickHandler = (user, value) => {
    setSelectedRow(user);
    setUser(user);
    setIsAddOrEdit(value);
    setShow(true);
  };
    

  const onSubmitHandler = (values, { resetForm }) => {
    
    let IsSystemUser = true;
    
    // Prepare the payload
    const usersObj = {
      FirstName: values.FirstName.trim(),
      LastName: values.LastName.trim(),
      CountryCodeID: values.CountryCodeID, 
      Mobile: values.Phone,
      Email: values.Email,
      GenderID: values.Gender, 
      RoleId: values.RoleID,     
      IsSystemUser,
      UserId: user.ID,
    };

    if (isAddOrEdit === "isEdit" && selectedRow) {
      usersObj["ID"] = selectedRow.ID; 
    }

    if (isAddOrEdit === "isAdd") {
      dispatch(createUser(usersObj));
    } else {
      dispatch(updateUser(usersObj));
    }
    resetForm();
    setShow(false);
  };

  const saveUserPreferences = async () => {
    const preferencesPayload = {
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ID] = selectedColumns.includes(column.ID);
        return acc;
      }, {}),
    };

    setModalOpen(false);
    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap();
      await dispatch(
        fetchUsersTableColumnNames({
          module: "SYSTEM_USER",
        })
      );

    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  const confirmDelete = () => {
    dispatch(deleteUser({ IDs: selectedIds }))
      .then(() => {
        setSelectedIds([]);
      })
      .catch((error) => {
        console.error("Error deleting:", error);
      });
    setDeleteModal(false);
  };

  const onActiveOrDeactiveChange = (userItem, e) => {
    setUserToToggle(userItem);
    setShowConfirmationModal(true);
  };

  const handleConfirmToggleStatus = async () => {
    if (userToToggle) {
      setToggleLoading(true);
      try {
        await dispatch(
          activateDeactivateUser({
            ID: userToToggle.ID,
            IsActive: !userToToggle.IsActive,
          })
        ).unwrap(); // Use unwrap to handle promise resolution/rejection
        // Optionally, refetch users or update UI based on success
      } catch (error) {
        console.error("Failed to toggle user status:", error);
        // Handle error (e.g., show a toast message)
      } finally {
        setToggleLoading(false);
        setShowConfirmationModal(false);
        setUserToToggle(null);
      }
    }
  };

  

  useEffect(() => {
    dispatch(
      fetchUsersTableColumnNames({
        module: "SYSTEM_USER",
      })
    );
    dispatch(fetchUsers({ IsSystemUser: true }));
    dispatch(fetchAllRoles({IsActive: true , hasAccess: true}));    
    dispatch(fetchGenders());
    dispatch(fetchCountry());
  }, [dispatch]);

  // Compute permission
  const permission = (() => {
    return usePermissions(1, 4);
  })();


  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(
          fetchUsersTableColumnNames({
            module: "SYSTEM_USER",
          })
        ).unwrap();
        await dispatch(fetchUsers({ IsSystemUser: true }));
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [dispatch]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={t("SystemUser")} />
          </Col>
        </Row>
        <div className="table-responsive"></div>

        <div className="table-responsive">
          <Card>
            <CardHeader>
                <Row className="g-4 ">
                  <Col className="col-sm-auto">
                    <div>
                      <Button
                        className="btn btn-soft-danger ms-2 me-2"
                        onClick={() => setDeleteModal(true)}
                        disabled={selectedIds.length === 0}
                      >
                        <i className="ri-delete-bin-2-line"></i>
                      </Button>
                      {permission.CanAdd && (
                        <Button
                          color="secondary"
                          className="add-btn me-1"
                          onClick={() => handleClassesClicks("isAdd")}
                          id="create-btn"
                          disabled={loading}
                        >
                          <i className="ri-add-line align-bottom me-1 ms-1"></i>
                          {t("AddNew")}
                        </Button>
                      )}
                    </div>
                  </Col>
                  <Col className="col-sm">
                    <div className="d-flex justify-content-sm-end">
                      <Button
                        className="btn btn-soft"
                        onClick={() => setModalOpen(true)}
                        disabled={loading}
                      >
                        <i className="ri-filter-3-line"></i>
                      </Button>
                      <div className="search-box ms-2 me-2">
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder={t("Search")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </Col>
                </Row>
            </CardHeader>

            <CardBody>
              <div className="listjs-table" id="customerList">
             
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner
                      style={{ height: "3rem", width: "3rem" }}
                      className="me-2"
                    >
                      Loading...
                    </Spinner>
                  </div>
                ) : (
                  <div className="listjs-table" id="customerList">
                    {/* <CommonTable
                      data={users}
                      tableColumnNames={tableColumnNames}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      hasAction={true}
                      permission={permission}
                    /> */}
                     <CommonDataTable
                      data={users}
                      tableColumnNames={tableColumnNames}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      actionConfig={actionConfig}
                      permission={permission}
                    />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>

      {/* Modal  */}
      <CommonAddModal
        show={show}
        setShow={setShow}
        onSubmitHandler={onSubmitHandler}
        labels={labels}
        isAddOrEdit={isAddOrEdit}
        selectedItemData={selectedRow}
        FormBody={AddSystemUserForm}
        title={isAddOrEdit === "isAdd" ? t("Add New System User") : t("Edit System User")}
        size="xl"
      />

      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={saveUserPreferences}
      />

      {/* Delete modal  */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        // description={
        //   selectedIds.length > 1
        //     ? `Are you sure you want to delete ${selectedIds.length} selected items?`
        //     : `Are you sure you want to delete this record?`
        // }
        description={t("confirmDelete")}
      />

      {/* Confirmation Modal for Activate/Deactivate */}
      <ConfirmationModal
        show={showConfirmationModal}
        setShow={setShowConfirmationModal}
        onConfirmClick={handleConfirmToggleStatus}
        item={userToToggle}
        loading={toggleLoading}
      />
    </div>
  );
}

export default SystemUser;
